import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Select,
  Snackbar,
  TextField
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMagnifyingGlass, faPlus, faXmark } from '@fortawesome/free-solid-svg-icons'
import LoadingIndicator from '../../common/LoadingIndicator'
import discountsRequest from './api'
import DiscountRow from './DiscountRow'
import DiscountDialog from './DiscountDialog'
import { capPartners, expirationState, filterDiscounts, swapItems } from './utils'

const MUTED = 'text-gray-500 dark:text-gray-400'
const CARD = 'bg-white dark:bg-dk-surface rounded-xl shadow-sm'

const toggleDescription = discount => {
  const lines = [
    discount.active
      ? 'Deja de aplicarse en la búsqueda, el detalle del viaje y el pago.'
      : 'Empieza a aplicarse en la búsqueda, el detalle del viaje y el pago.',
    'El cambio llega a la app en hasta 1 minuto.'
  ]
  if (discount.requires_notified_lobby_view) {
    lines.push(
      discount.active
        ? 'También deja de mandarse el push de vista abandonada en esta ruta.'
        : 'También se prende el push de vista abandonada en esta ruta.'
    )
  }
  if (!discount.active && expirationState(discount.expires_at)?.expired) {
    lines.push('La fecha de vencimiento ya pasó, pero es informativa: el descuento se aplica igual.')
  }
  return lines
}

export default function Discounts() {
  const [discounts, setDiscounts] = useState(null)
  const [loadError, setLoadError] = useState(false)
  const [query, setQuery] = useState('')
  const [status, setStatus] = useState('active')
  const [editing, setEditing] = useState(null)
  const [toggling, setToggling] = useState(null)
  const [busyId, setBusyId] = useState(null)
  const [reordering, setReordering] = useState(false)
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' })

  const notify = (message, severity = 'success') => setToast({ open: true, message, severity })

  const load = useCallback(async () => {
    setLoadError(false)
    const { ok, data } = await discountsRequest()
    if (ok && Array.isArray(data)) setDiscounts(data)
    else setLoadError(true)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const visible = useMemo(
    () => (discounts ? filterDiscounts(discounts, { query, status }) : []),
    [discounts, query, status]
  )
  const canReorder = !query.trim()
  const activeCount = discounts?.filter(discount => discount.active).length ?? 0
  const takenCodes = useMemo(() => (discounts || []).map(discount => discount.code), [discounts])

  const replaceDiscount = saved =>
    setDiscounts(previous =>
      previous.map(discount => (discount._id === saved._id ? saved : discount))
    )

  const confirmToggle = async () => {
    const discount = toggling
    setToggling(null)
    setBusyId(discount._id)
    const { ok, data } = await discountsRequest(`/${discount._id}`, {
      method: 'PATCH',
      body: { active: !discount.active }
    })
    setBusyId(null)
    if (ok && data?._id) {
      replaceDiscount(data)
      notify(`${data.code} ${data.active ? 'prendido' : 'apagado'}`)
    } else {
      notify(`No se pudo ${discount.active ? 'apagar' : 'prender'} ${discount.code}`, 'error')
    }
  }

  const move = async (discount, neighbor) => {
    const previous = discounts
    const next = swapItems(discounts, discount, neighbor)
    if (next === previous) return
    setDiscounts(next)
    setReordering(true)
    const { ok, data } = await discountsRequest('/order', {
      method: 'PUT',
      body: { ids: next.map(discount => discount._id) }
    })
    setReordering(false)
    if (ok) return
    setDiscounts(previous)
    notify(data?.errors?.[0] || 'No se pudo cambiar el orden', 'error')
  }

  const shareCap = async saved => {
    const partners = capPartners(discounts, saved)
    const results = await Promise.all(
      partners.map(partner =>
        discountsRequest(`/${partner._id}`, {
          method: 'PATCH',
          body: { shares_limit_with: [...(partner.shares_limit_with || []), saved.code] }
        })
      )
    )
    results.forEach(({ ok, data }) => ok && data?._id && replaceDiscount(data))
    return partners.filter((_, index) => !results[index].ok || !results[index].data?._id)
  }

  const handleSaved = async (saved, { created }) => {
    const duplicated = !!editing?.source
    setEditing(null)
    if (created) {
      setDiscounts(previous => [...previous, saved])
      if (filterDiscounts([saved], { query, status }).length === 0) {
        setQuery('')
        setStatus('all')
      }
    } else {
      replaceDiscount(saved)
    }
    if (!duplicated) {
      notify(created ? `${saved.code} creado` : `${saved.code} guardado`)
      return
    }
    const failed = await shareCap(saved)
    if (failed.length > 0) {
      notify(
        `${saved.code} creado, pero no se pudo sumar al tope de ${failed
          .map(discount => discount.code)
          .join(', ')}`,
        'warning'
      )
    } else {
      notify(`${saved.code} creado`)
    }
  }

  if (!discounts) {
    return loadError ? (
      <Container maxWidth='md' sx={{ py: 4 }}>
        <div className={`${CARD} p-6 flex flex-col items-start gap-3`}>
          <p className='text-base font-semibold text-gray-800 dark:text-gray-100'>
            No se pudieron cargar los descuentos
          </p>
          <p className={`text-sm ${MUTED}`}>
            Puede ser un problema de conexión o que tu usuario no tenga el permiso de descuentos.
          </p>
          <button
            className='px-3 py-2 rounded-lg bg-primary text-white hover:opacity-90 transition-opacity'
            onClick={load}
          >
            Reintentar
          </button>
        </div>
      </Container>
    ) : (
      <div className='h-full flex items-center justify-center'>
        <LoadingIndicator />
      </div>
    )
  }

  return (
    <Container
      maxWidth={false}
      sx={{
        height: 'calc(100vh - 4rem)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        width: '100%',
        maxWidth: '1080px',
        px: { xs: 2, md: 3 }
      }}
    >
      <div className={`${CARD} p-4 mt-6 mb-2 flex flex-wrap items-center gap-3`}>
        <TextField
          size='small'
          placeholder='Buscar código o ciudad'
          value={query}
          onChange={event => setQuery(event.target.value)}
          className='w-full sm:w-72'
          inputProps={{ 'aria-label': 'Buscar código o ciudad' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <FontAwesomeIcon icon={faMagnifyingGlass} className={`text-sm ${MUTED}`} />
              </InputAdornment>
            ),
            endAdornment: query ? (
              <InputAdornment position='end'>
                <button
                  className={`w-7 h-7 -mr-1 rounded-md flex items-center justify-center hover:bg-gray-100 dark:hover:bg-dk-hover ${MUTED}`}
                  onClick={() => setQuery('')}
                  aria-label='Limpiar búsqueda'
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </InputAdornment>
            ) : null
          }}
        />
        <Select
          size='small'
          value={status}
          onChange={event => setStatus(event.target.value)}
          inputProps={{ 'aria-label': 'Estado' }}
        >
          <MenuItem value='all'>Todos</MenuItem>
          <MenuItem value='active'>Activos</MenuItem>
          <MenuItem value='inactive'>Apagados</MenuItem>
        </Select>
        <span className={`text-sm ${MUTED}`}>
          {activeCount} de {discounts.length} activos
        </span>
        <button
          className='sm:ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white hover:opacity-90 transition-opacity'
          onClick={() => setEditing({ discount: null })}
        >
          <FontAwesomeIcon icon={faPlus} className='text-xs' />
          Nuevo descuento
        </button>
      </div>

      <p className={`text-xs px-1 mb-3 ${MUTED}`}>
        Se aplica el primero de la lista que coincida con el viaje y con el usuario. Los cambios
        llegan a la app en hasta 1 minuto.
      </p>

      {visible.length > 0 ? (
        <ul className={`${CARD} divide-y divide-gray-100 dark:divide-dk-border mb-6`}>
          {visible.map((discount, visibleIndex) => (
            <DiscountRow
              key={discount._id}
              discount={discount}
              position={discounts.indexOf(discount) + 1}
              isFirst={visibleIndex === 0}
              isLast={visibleIndex === visible.length - 1}
              canReorder={canReorder}
              busy={reordering || busyId === discount._id}
              onMove={delta => move(discount, visible[visibleIndex + delta])}
              onToggle={() => setToggling(discount)}
              onEdit={() => setEditing({ discount })}
              onDuplicate={() => setEditing({ discount: null, source: discount })}
            />
          ))}
        </ul>
      ) : (
        <div className={`${CARD} px-6 py-10 text-center`}>
          <p className='text-sm font-medium text-gray-800 dark:text-gray-100'>
            {discounts.length === 0 ? 'Todavía no hay descuentos' : 'Ningún descuento coincide'}
          </p>
          {discounts.length > 0 && (
            <button
              className='mt-2 text-sm text-primary hover:underline'
              onClick={() => {
                setQuery('')
                setStatus('all')
              }}
            >
              Ver todos
            </button>
          )}
        </div>
      )}

      {editing && (
        <DiscountDialog
          discount={editing.discount}
          source={editing.source}
          takenCodes={takenCodes}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
        />
      )}

      <Dialog open={!!toggling} onClose={() => setToggling(null)} maxWidth='xs' fullWidth>
        {toggling && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              {toggling.active ? 'Apagar' : 'Prender'} {toggling.code}
            </DialogTitle>
            <DialogContent>
              {toggleDescription(toggling).map(line => (
                <p key={line} className={`text-sm mt-1 ${MUTED}`}>
                  {line}
                </p>
              ))}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
              <button
                className='px-3 py-2 rounded-lg bg-light_gray dark:bg-dk-raised text-black dark:text-gray-100 hover:opacity-90 transition-opacity'
                onClick={() => setToggling(null)}
              >
                Cancelar
              </button>
              <button
                className='px-4 py-2 rounded-lg bg-primary text-white hover:opacity-90 transition-opacity'
                onClick={confirmToggle}
              >
                {toggling.active ? 'Apagar' : 'Prender'}
              </button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={(_, reason) => {
          if (reason !== 'clickaway') setToast(previous => ({ ...previous, open: false }))
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={toast.severity}
          variant='filled'
          onClose={() => setToast(previous => ({ ...previous, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}
